import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { FaStethoscope } from 'react-icons/fa6';
import { MdMovieEdit } from 'react-icons/md';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';

import { get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import SmsFailedRoundedIcon from '@mui/icons-material/SmsFailedRounded';
import { Box, Button, Divider, Drawer, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import { RootState } from '../../../../app/rootReducer';
import { AppDispatch } from '../../../../app/store';
import {
  FlexCol,
  FlexGrid,
  FlexRow,
  NoraButton,
  Spacer,
} from '../../../../common/components/v2';
import {
  Decision,
  PA,
  RateClass,
  TobaccoClass,
} from '../../../../common/constants/decision';
import { Products } from '../../../../common/constants/products';
import { Resource } from '../../../../common/constants/resource';
import ElementLocators from '../../../../common/ElementLocators';
import { ProductFactory } from '../../../../common/productConfig/productFactory';
import {
  getValidationResult,
  validateMIBCodes,
} from '../../decision/decisionSlice';
import { useDecisionSubmit } from '../../decision/useDecisionSubmit';
import { IDecision } from '../../interfaces/IDecision';
import { IDecisionRequest } from '../../interfaces/IDecisionRequest';
import { IDecisionUpdateRequest } from '../../interfaces/IDecisionUpdateRequest';
import { IFilterItem } from '../../interfaces/IFilterItem';
import { IRiderInfo } from '../../interfaces/IRiderInfo';
import { ReasonsSummary } from '../ReasonsSummary';
import styles from '../styles.module.scss';

const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export const ReviewApproval = ({
  applicationId,
  isOpen,
  setIsOpen,
  setIsReview,
  product,
  carrier,
  subCategory,
  maxCoverage,
  term,
  selectedRateClass,
  selectedTobaccoClass,
  selectedPaOption,
  debits,
  amendment,
  doctorInfo,
  showOtaf,
  selectedOtafReasons,
  otafDescription,
  isPiaOrPlanF,
  mibCodes,
  editedDecision,
  updateReason,
  riders,
  engineMaxCoverage,
  isEditLeadingToCLR,
  triggerClr,
  haltClrReasons,
  isPolicyActivated,
}: {
  applicationId: string;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setIsReview: Dispatch<SetStateAction<boolean>>;
  product: string;
  carrier: string;
  subCategory: string;
  maxCoverage: number;
  term: number;
  selectedRateClass?: IFilterItem | null;
  selectedTobaccoClass?: IFilterItem | null;
  selectedPaOption: IFilterItem;
  debits: number | null;
  amendment: string;
  doctorInfo: string;
  showOtaf: boolean;
  selectedOtafReasons: string[];
  otafDescription: string;
  isPiaOrPlanF: boolean;
  mibCodes: string[];
  editedDecision?: IDecision;
  updateReason: string | null | undefined;
  riders?: IRiderInfo[];
  engineMaxCoverage?: number | null;
  isEditLeadingToCLR?: boolean;
  triggerClr?: boolean;
  haltClrReasons?: string[];
  isPolicyActivated?: boolean;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const productConfig = ProductFactory.getProductConfig(
    carrier,
    product,
    subCategory,
  );
  const isIulProduct = [Products.IUL, Products.NAIULACUM].includes(
    product as Products,
  );
  const paValue = productConfig?.showPercentageAddition(
    selectedRateClass?.label,
  )
    ? selectedPaOption.label
    : Resource.NORA_M_DASH;

  const debitsValue = productConfig?.showDebits(selectedRateClass?.label)
    ? debits
    : Resource.NORA_M_DASH;

  // For rate classes other than standard send PA100 as default to the backend.
  const tableRating = paValue === undefined ? PA.PA100 : paValue;

  const codeback = useSelector(state => state.decision);
  // Doctor Info and Amendment do not apply to PIA / Plan F decisions
  const doctorInfoAndAmendment = isPiaOrPlanF ? {} : { doctorInfo, amendment };
  const { submitCreate, submitEdit } = useDecisionSubmit(applicationId, 'approve');
  const eligibleRiders = (riders || [])
    ?.filter(r => r.isEligible && r.isUnderwritable)
    .map(r => r.rider);
  const ineligibleRiders = (riders || [])
    ?.filter(r => !r.isEligible && r.isUnderwritable)
    .map(r => r.rider);
  let resultMap: Map<string, object> = new Map();
  useEffect(() => {
    if (mibCodes?.length > 0) {
      resultMap = new Map();
      dispatch(validateMIBCodes(applicationId, mibCodes));
    }
  }, [mibCodes]);

  return (
    <Drawer
      open={isOpen}
      anchor="right"
      variant="persistent"
      classes={{ paper: styles.Drawer }}
      transitionDuration={{ enter: 0, exit: 0 }}
    >
      <Box mt={6}>
        <Box
          m={3}
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body1">
            {Resource.APPROVE_APPLICATION_HEADER}
          </Typography>
          <Button onClick={() => setIsReview(false)}>
            <Typography variant="subtitle2" color="textSecondary">
              Back
            </Typography>
          </Button>
        </Box>
        <Divider />
        <Box m={3}>
          <FlexGrid>
            <Spacer height={16} />
            {Boolean(editedDecision) && (
              <>
                <FlexRow className={styles.DrawerSubTitle}>
                  {Resource.UPDATE_REASON_HEADER}
                </FlexRow>
                <FlexRow>
                  <p>{updateReason}</p>
                </FlexRow>
                <Spacer height={16} />
              </>
            )}
            <FlexRow className={styles.DrawerSubTitle}>APPROVE AT</FlexRow>

            <FlexRow>
              <FlexCol sm={4} lg={4} className={styles.ColumnLabel}>
                <p>Max Coverage</p>
              </FlexCol>
              <FlexCol sm={6} lg={6} className={styles.ColumnInfo}>
                <p>{`$${maxCoverage.toLocaleString()}`}</p>
              </FlexCol>
            </FlexRow>
            {engineMaxCoverage && maxCoverage > engineMaxCoverage && (
              <FlexRow style={{ marginTop: '-18px' }}>
                <p style={{ color: 'red', textAlign: 'left' }}>
                  Max coverage is higher than Engine calculation!
                </p>
              </FlexRow>
            )}
            {!isIulProduct && (
              <FlexRow>
                <FlexCol sm={4} lg={4} className={styles.ColumnLabel}>
                  <p>Term Length</p>
                </FlexCol>
                <FlexCol sm={6} lg={6} className={styles.ColumnInfo}>
                  <p>{term ? term : '-'}</p>
                </FlexCol>
              </FlexRow>
            )}
            {productConfig?.hasRateClass() && (
              <FlexRow>
                <FlexCol sm={4} lg={4} className={styles.ColumnLabel}>
                  <p>Class</p>
                </FlexCol>
                <FlexCol sm={6} lg={6} className={styles.ColumnInfo}>
                  <p>{selectedRateClass?.label}</p>
                </FlexCol>
              </FlexRow>
            )}
            {productConfig?.hasRateClass() && (
              <FlexRow>
                <FlexCol sm={4} lg={4} className={styles.ColumnLabel}>
                  <p>Tobacco</p>
                </FlexCol>
                <FlexCol sm={6} lg={6} className={styles.ColumnInfo}>
                  <p>{selectedTobaccoClass?.label}</p>
                </FlexCol>
              </FlexRow>
            )}
            {productConfig?.hasPercentageAddition() && (
              <FlexRow>
                <FlexCol sm={4} lg={4} className={styles.ColumnLabel}>
                  <p>% Additional</p>
                </FlexCol>
                <FlexCol sm={6} lg={6} className={styles.ColumnInfo}>
                  <p>{paValue}</p>
                </FlexCol>
              </FlexRow>
            )}
            {productConfig?.showDebits(selectedRateClass?.label) && (
              <FlexRow>
                <FlexCol sm={4} lg={4} className={styles.ColumnLabel}>
                  <p>Debits</p>
                </FlexCol>
                <FlexCol sm={6} lg={6} className={styles.ColumnInfo}>
                  <p>{debitsValue}</p>
                </FlexCol>
              </FlexRow>
            )}
            <Spacer height={16} />
            {eligibleRiders && eligibleRiders.length > 0 && (
              <>
                <FlexRow className={styles.DrawerSubTitle}>
                  {Resource.ELIGIBLE_RIDERS}
                </FlexRow>
                {eligibleRiders.map(rider => (
                  <FlexRow key={rider}>
                    <span className={styles.Info}>{`${rider}`}</span>
                  </FlexRow>
                ))}
              </>
            )}
            {ineligibleRiders && ineligibleRiders.length > 0 && (
              <>
                <FlexRow className={styles.DrawerSubTitle}>
                  {Resource.INELIGIBLE_RIDERS}
                </FlexRow>
                {ineligibleRiders.map(rider => (
                  <FlexRow key={rider}>
                    <span className={styles.Info}>{`${rider}`}</span>
                  </FlexRow>
                ))}
              </>
            )}
            {mibCodes && mibCodes.length > 0 && (
              <>
                <FlexRow className={styles.DrawerSubTitle}>
                  {Resource.MIB_CODES}
                </FlexRow>
                <Spacer height={8} />
                {mibCodes.map(mibCode => (
                  <FlexRow key={uuidv4()}>
                    <span className={styles.Info}>
                      {`# ${mibCode}`}
                      {get(codeback, 'validationFailed', false) ? (
                        <Tooltip
                          title={'Validation failed, you could still submit'}
                        >
                          <SmsFailedRoundedIcon
                            fontSize={'small'}
                            color={'info'}
                            sx={{ marginLeft: '15px', marginBottom: '-5px' }}
                          ></SmsFailedRoundedIcon>
                        </Tooltip>
                      ) : (
                        getValidationResult(mibCode, codeback, resultMap)
                      )}
                    </span>
                  </FlexRow>
                ))}
              </>
            )}
            {showOtaf && (
              <>
                <ReasonsSummary
                  title={Resource.OTAF_REASON}
                  reasons={selectedOtafReasons}
                  description={otafDescription}
                />
                <Spacer height={24} />
              </>
            )}
            {amendment && (
              <>
                <FlexRow className={styles.DrawerSubTitle}>
                  <label>AMENDMENT</label>
                </FlexRow>
                <Spacer height={8} />
                <FlexRow>
                  <FlexCol xs={1} sm={1} lg={1}>
                    <MdMovieEdit />
                  </FlexCol>
                  <FlexCol xs={11} sm={11} lg={11} className={styles.Info}>
                    <span>{amendment}</span>
                  </FlexCol>
                </FlexRow>
              </>
            )}
            {triggerClr === false && (
              <>
                <Spacer height={24} />
                <FlexRow className={styles.DrawerSubTitle}>
                  <label>CLR STATUS</label>
                </FlexRow>
                <Spacer height={8} />
                <FlexRow>
                  <FlexCol xs={12}>
                    <Typography color="warning.main">
                      Closed Loop Rescission will NOT be triggered
                    </Typography>
                  </FlexCol>
                </FlexRow>
                {haltClrReasons && haltClrReasons.length > 0 && (
                  <>
                    <Spacer height={16} />
                    <ReasonsSummary
                      title="CLR HALT REASONS"
                      reasons={haltClrReasons}
                    />
                  </>
                )}
              </>
            )}
            {!isPiaOrPlanF && (
              <>
                <Spacer height={24} />
                <FlexRow className={styles.DrawerSubTitle}>
                  <label>DOCTOR'S INFO (POST ISSUE APS)</label>
                </FlexRow>
                <Spacer height={8} />
                <FlexRow>
                  <FlexCol xs={1} sm={1} lg={1}>
                    <FaStethoscope />
                  </FlexCol>
                  <FlexCol xs={11} sm={11} lg={11} className={styles.Info}>
                    <span>{doctorInfo}</span>
                  </FlexCol>
                </FlexRow>
              </>
            )}
          </FlexGrid>
        </Box>
      </Box>
      <Box>
        <NoraButton
          name={ElementLocators.LOCATOR_SUBMIT_APPROVAL_BUTTON}
          className={styles.DrawerButton}
          onClick={() => {
            const haltClr = triggerClr === false;
            const haltClrReasonsToSend = haltClr ? haltClrReasons : undefined;

            if (editedDecision) {
              const decisionUpdate = {
                decision: {
                  decision: Decision.APPROVED,
                  decisionType: editedDecision.decisionType,
                  rateClass: productConfig?.hasRateClass()
                    ? (selectedRateClass?.label as RateClass)
                    : null,
                  tobaccoClass: productConfig?.hasRateClass()
                    ? (selectedTobaccoClass?.label as TobaccoClass)
                    : null,
                  term,
                  tableRating: productConfig?.hasTableRating()
                    ? tableRating
                    : null,
                  debits: productConfig?.hasDebits() ? debits : null,
                  ...doctorInfoAndAmendment,
                  coverage: maxCoverage,
                  otafReasons: showOtaf ? selectedOtafReasons : undefined,
                  otafDescription: showOtaf ? otafDescription : undefined,
                  mibCodes,
                  ridersInfo: riders,
                  reviewStatus: editedDecision.reviewStatus,
                  ...(isPolicyActivated && {
                    haltClr,
                    haltClrReasons: haltClrReasonsToSend,
                  }),
                } as IDecisionRequest,
                reason: updateReason,
              } as IDecisionUpdateRequest;
              submitEdit(editedDecision.id.toString(), decisionUpdate, isEditLeadingToCLR);
            } else {
              submitCreate(
                {
                  paValue,
                  selectedRateClass,
                  selectedTobaccoClass,
                  term,
                  debits,
                  ...doctorInfoAndAmendment,
                  maxCoverage,
                  showOtaf,
                  selectedOtafReasons,
                  otafDescription,
                  mibCodes,
                  riders,
                  triggerClr,
                  haltClrReasons,
                },
                productConfig,
                isPolicyActivated,
              );
            }
            setIsReview(false);
            setIsOpen(false);
          }}
        >
          Approve Application
        </NoraButton>
      </Box>
    </Drawer>
  );
};
